import { useEffect, useRef, useState } from "react";
import { MdOutlineFileUpload, MdOutlineDelete } from "react-icons/md";

export default function ImportCourseImage({
  file,
  url,
  onChange,
  rowNumber,
  error,
}) {
  const fileInput = useRef(null);
  const [preview, setPreview] = useState("");
  const [fileError, setFileError] = useState("");
  const [previewFailed, setPreviewFailed] = useState(false);
  useEffect(() => {
    setPreviewFailed(false);
    if (!file) {
      setPreview("");
      return undefined;
    }
    const objectUrl = URL.createObjectURL(file);
    setPreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file, url]);
  const chooseFile = (event) => {
    const selected = event.target.files?.[0];
    event.target.value = "";
    if (!selected) return;
    if (!selected.type.startsWith("image/")) {
      setFileError("Choose an image file.");
      return;
    }
    if (selected.size > 10 * 1024 * 1024) {
      setFileError("Choose an image smaller than 10 MB.");
      return;
    }
    setFileError("");
    onChange({ courseImageFile: selected, courseImageUrl: "" });
  };
  return (
    <div className="min-w-0 text-center text-sm">
      <label
        htmlFor={`import-image-url-${rowNumber}`}
        className="mb-2 block font-medium"
      >
        Course image
      </label>
      <input
        id={`import-image-url-${rowNumber}`}
        type="url"
        aria-label={`Image URL row ${rowNumber}`}
        placeholder="Paste an image URL, or upload below"
        value={url}
        disabled={!!file}
        onChange={(event) => {
          setFileError("");
          onChange({
            courseImageUrl: event.target.value,
            courseImageFile: null,
          });
        }}
        className="w-full min-w-0 rounded-lg border border-slate-400/40 bg-white p-2 text-sm text-slate-900 disabled:opacity-50"
      />
      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        aria-label={`Upload image row ${rowNumber}`}
        className="sr-only"
        onChange={chooseFile}
      />
      <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
        <button
          type="button"
          onClick={() => fileInput.current?.click()}
          className="inline-flex items-center gap-1 rounded-lg border border-blue-500/40 px-3 py-2 text-xs font-semibold text-blue-500 hover:bg-blue-500/10"
        >
          <MdOutlineFileUpload size={18} aria-hidden="true" />
          {file ? "Replace image" : "Upload image"}
        </button>
        {(file || url) && (
          <button
            type="button"
            onClick={() => {
              setFileError("");
              onChange({ courseImageFile: null, courseImageUrl: "" });
            }}
            aria-label={`Remove image row ${rowNumber}`}
            className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-xs text-red-500 hover:bg-red-500/10"
          >
            <MdOutlineDelete size={18} aria-hidden="true" />
            Remove
          </button>
        )}
      </div>
      {preview && !previewFailed && (
        <img
          src={preview}
          alt=""
          onError={() => setPreviewFailed(true)}
          className="mx-auto mt-3 h-24 max-w-full rounded-lg object-contain"
        />
      )}
      {file && (
        <p className="mt-2 break-all text-xs text-slate-500">
          {file.name} · Uploaded when you create the courses.
        </p>
      )}
      {!file && !url && (
        <p className="mt-2 text-xs text-slate-500">
          No image selected. The default course image will be used.
        </p>
      )}
      {previewFailed && (
        <p className="mt-2 text-xs text-amber-600">
          Preview unavailable. Choose another image if needed.
        </p>
      )}
      {(fileError || error) && (
        <p role="alert" className="mt-2 text-xs text-red-500">
          {fileError || error}
        </p>
      )}
    </div>
  );
}
